import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Facebook,
  Instagram,
  LayoutDashboard,
  Mail,
  MapPin,
  Phone,
} from 'lucide-react';

function TikTokIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.77 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z" />
    </svg>
  );
}

function XIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}
import BrandLogo from '../BrandLogo.jsx';
import BrandName from './BrandName.jsx';
import HoverTooltip from '../ui/HoverTooltip.jsx';
import LegalPolicyModal from './LegalPolicyModal.jsx';
import { dataTreatmentPolicy } from '../../content/legal/dataTreatmentPolicy.js';
import { privacyPolicy } from '../../content/legal/privacyPolicy.js';
import { BRAND_LEGAL_NAME, BRAND_SHORT_NAME } from '../../constants/brand.js';
import { PUBLIC_NAV_LINKS } from '../../constants/navigation.js';
import {
  DEPARTMENT_CONTACT_LINES,
  getDepartmentWhatsAppHref,
  OFFICE_LOCATION_LABEL,
  OFFICE_MAPS_URL,
  SUPERSOLIDARIA_LOGO,
  SUPERSOLIDARIA_NAME,
  SUPERSOLIDARIA_URL,
} from '../../constants/contact.js';
import { FOOTER_FLOAT_INSET_X_CLASS, FOOTER_FLOAT_PADDING_CLASS } from '../../constants/floatingActions.js';
import { formatPhoneDisplay, getTelHref, WHATSAPP_PHONE } from '../../constants/whatsappContact.js';

/** Sustituye las URLs cuando tengas los perfiles oficiales. */
const SOCIAL_LINKS = [
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/people/Coonaltragas-Coop/61562271791934/?rdid=2X7TGGGZAjquGw9y&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F15yKsTt1do%2F',
    Icon: Facebook,
    brandHover:
      'hover:border-[#1877F2] hover:bg-[#1877F2] hover:text-white',
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/cooperativa.coonaltragas/',
    Icon: Instagram,
    brandHover:
      'hover:border-[#E1306C] hover:bg-[#E1306C] hover:text-white',
  },
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com/@coonaltragascooperativa',
    Icon: TikTokIcon,
    brandHover:
      'hover:border-black hover:bg-black hover:text-white',
  },
  {
    label: 'X',
    href: 'https://x.com/Coonaltragas',
    Icon: XIcon,
    brandHover:
      'hover:border-black hover:bg-black hover:text-white',
  },
];

/** Base del pie; superficies un poco más claras para tarjetas y franja inferior */
const BASE = '#101218';
const SURFACE = '#161a22';
const SURFACE_DEEP = '#0b0d10';

export default function Footer() {
  const [legalModal, setLegalModal] = useState(null);

  return (
    <footer
      className="relative overflow-hidden border-t-[3px] border-[#1acb12] text-slate-200 shadow-[0_-16px_48px_rgba(0,0,0,0.5)]"
      style={{ backgroundColor: BASE }}
    >
      {/* Acento de marca muy suave sobre el gris base */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.22]"
        aria-hidden
        style={{
          background: `radial-gradient(ellipse 85% 55% at 0% 100%, rgba(26,203,18,0.2), transparent 52%),
            radial-gradient(ellipse 75% 50% at 100% 0%, rgba(13,43,127,0.28), transparent 50%)`,
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        aria-hidden
        style={{
          background: `linear-gradient(165deg, transparent 0%, rgba(255,255,255,0.02) 45%, transparent 70%)`,
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 py-14 lg:px-8">
        <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div
                className="shrink-0 rounded-xl p-2.5 ring-1 ring-white/12 backdrop-blur-sm"
                style={{ backgroundColor: `${SURFACE}e6` }}
              >
                <BrandLogo className="max-h-9 max-w-[9.5rem] lg:max-h-10 lg:max-w-[10.5rem]" alt={BRAND_SHORT_NAME} />
              </div>
              <BrandName
                variant="onDark"
                className="pt-0.5"
                shortNameClassName="text-xl lg:text-2xl"
                legalClassName="text-sm"
              />
            </div>
            <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400">
              Garantizando eficiencia, calidad y modernización en la gestión de los servicios, siendo la expresión orgánica del sector en bienestar y materia Cooperativa.
            </p>
            <div className="mt-6">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Vigilada por
              </p>
              <HoverTooltip label="Ver sitio web" placement="bottom" className="mt-2">
                <a
                  href={SUPERSOLIDARIA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${SUPERSOLIDARIA_NAME} — sitio oficial`}
                  className="inline-block rounded-lg bg-white/95 p-2 ring-1 ring-white/20 transition hover:bg-white hover:ring-[#1acb12]/40"
                >
                  <img
                    src={SUPERSOLIDARIA_LOGO}
                    alt={SUPERSOLIDARIA_NAME}
                    className="h-9 w-auto max-w-[11rem] object-contain object-left sm:h-10 sm:max-w-[12rem]"
                    width={192}
                    height={40}
                    loading="lazy"
                    decoding="async"
                  />
                </a>
              </HoverTooltip>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-slate-100">Navegación</h3>
            <ul className="mt-5 space-y-3 text-base">
              {PUBLIC_NAV_LINKS.map((item) => (
                <li key={item.to + item.label}>
                  <Link
                    to={item.to}
                    className="text-slate-400 transition hover:text-slate-100 hover:underline decoration-[#1acb12]/80 underline-offset-4"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-slate-100">Contacto</h3>
            <ul className="mt-5 space-y-4 text-base text-slate-400">
              <li className="flex gap-3">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-accent-lime" aria-hidden />
                <HoverTooltip label="Ver ubicación" placement="bottom">
                  <a
                    href={OFFICE_MAPS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Ver ubicación: ${OFFICE_LOCATION_LABEL}`}
                    className="transition hover:text-slate-100 hover:underline decoration-[#1acb12]/80 underline-offset-4"
                  >
                    {OFFICE_LOCATION_LABEL}
                  </a>
                </HoverTooltip>
              </li>
              {WHATSAPP_PHONE ? (
                <li className="flex items-center gap-3">
                  <Phone className="h-5 w-5 shrink-0 text-accent-lime" aria-hidden />
                  <a href={getTelHref()} className="transition hover:text-slate-100">
                    (601) 4462466
                  </a>
                </li>
              ) : null}
              {DEPARTMENT_CONTACT_LINES.map((line) => (
                <li key={line.telDigits} className="flex gap-3">
                  <Phone className="mt-0.5 h-5 w-5 shrink-0 text-accent-lime" aria-hidden />
                  <div>
                    <span className="block text-sm text-slate-500">{line.label}</span>
                    <a
                      href={getDepartmentWhatsAppHref(line)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="transition hover:text-slate-100 hover:underline decoration-[#1acb12]/80 underline-offset-4"
                      aria-label={`${line.label} por WhatsApp`}
                    >
                      {line.display}
                    </a>
                  </div>
                </li>
              ))}
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 shrink-0 text-accent-lime" aria-hidden />
                <a href="mailto:gerencia@coonaltragas.com" className="transition hover:text-slate-100">
                  gerencia@coonaltragas.com
                </a>
              </li>
            </ul>
          </div>

          <div className="flex flex-col gap-8 lg:col-span-3">
            <div>
              <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-slate-100">Redes sociales</h3>
              <p className="mt-2 text-sm text-slate-500">Síguenos y entérate de campañas y noticias.</p>
              <ul className="mt-4 flex flex-wrap gap-2">
                {SOCIAL_LINKS.map(({ label, href, Icon, brandHover }) => {
                  const isExternal = href.startsWith('http');
                  return (
                    <li key={label}>
                      <a
                        href={href}
                        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        onClick={!isExternal ? (e) => e.preventDefault() : undefined}
                        aria-label={label}
                        className={`flex h-11 w-11 items-center justify-center rounded-xl border border-white/12 bg-[#161a22] text-slate-200 transition-colors duration-200 ${brandHover}`}
                      >
                        <Icon className="h-5 w-5" strokeWidth={1.75} />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div
        className="relative border-t border-white/[0.08]"
        style={{ backgroundColor: SURFACE_DEEP }}
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px opacity-60"
          aria-hidden
          style={{
            background: 'linear-gradient(90deg, transparent, rgba(26,203,18,0.35), rgba(13,43,127,0.4), transparent)',
          }}
        />
        <div
          className={`mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 py-5 text-sm font-medium text-slate-500 sm:flex-row sm:px-8 ${FOOTER_FLOAT_PADDING_CLASS} ${FOOTER_FLOAT_INSET_X_CLASS}`}
        >
          <p className="max-w-xl text-center sm:text-left">
            © {new Date().getFullYear()}{' '}
            <span className="font-semibold text-slate-300">{BRAND_SHORT_NAME}</span>
            <span className="mt-1 block text-slate-500 sm:mt-0 sm:inline">
              {' '}
              — {BRAND_LEGAL_NAME}. Todos los derechos reservados.
            </span>
          </p>
          <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2" aria-label="Legal">
            <button
              type="button"
              onClick={() => setLegalModal('privacy')}
              className="transition hover:text-accent-lime"
            >
              Política de privacidad
            </button>
            <button
              type="button"
              onClick={() => setLegalModal('treatment')}
              className="transition hover:text-accent-lime"
            >
              Tratamiento de datos
            </button>
            <Link to="/contacto" className="transition hover:text-accent-lime">
              PQRS
            </Link>
            <Link
              to="/admin/dashboard"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-md p-1.5 text-slate-400 transition hover:bg-white/[0.06] hover:text-slate-200"
              aria-label="Acceso al panel de administración"
            >
              <LayoutDashboard className="h-4 w-4" strokeWidth={1.75} aria-hidden />
            </Link>
          </nav>
        </div>
      </div>
      <LegalPolicyModal
        open={legalModal === 'privacy'}
        onClose={() => setLegalModal(null)}
        policy={privacyPolicy}
      />
      <LegalPolicyModal
        open={legalModal === 'treatment'}
        onClose={() => setLegalModal(null)}
        policy={dataTreatmentPolicy}
      />
    </footer>
  );
}
